/**
 * src/lib/voice/voiceSession.js — Phase 76-02 Task 3
 *
 * Supabase helpers for `voice_sessions` rows. Pulled out of
 * useVoiceConversation so the parent hook stays a thin orchestrator.
 * Each function is a plain async that takes supabase + params.
 */

export async function ensureVoiceSession({ supabase, existingId, contextProjectId, contextDealId }) {
  if (existingId) return existingId;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase
    .from('voice_sessions')
    .insert({
      user_id: user.id,
      project_id: contextProjectId || null,
      deal_id: contextDealId || null,
      turn_count: 0,
      transcript: [],
    })
    .select('id')
    .single();
  if (error) {
    console.error('[voiceSession] ensure error:', error.message);
    return null;
  }
  return data.id;
}

/**
 * Migration 089 / B4: ensure a chat_conversations row exists for this voice
 * session so each user/assistant turn can be persisted via appendMessage().
 * Links voice_sessions.chat_conversation_id so the envelope knows where its
 * transcript lives. Idempotent — returns existing id if already linked.
 */
export async function ensureChatConversationForVoice({
  supabase,
  voiceSessionId,
  existingChatId,
  contextProjectId,
  contextDealId,
}) {
  if (existingChatId) return existingChatId;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: convo, error: convoErr } = await supabase
    .from('chat_conversations')
    .insert({
      user_id: user.id,
      project_id: contextProjectId || null,
      deal_id: contextDealId || null,
      title: 'Voice session',
    })
    .select('id')
    .single();
  if (convoErr) {
    console.error('[voiceSession] ensureChatConversation error:', convoErr.message);
    return null;
  }

  if (voiceSessionId) {
    // Fire-and-forget link; failure doesn't block transcript persistence.
    supabase
      .from('voice_sessions')
      .update({ chat_conversation_id: convo.id })
      .eq('id', voiceSessionId)
      .then(({ error: linkErr }) => {
        if (linkErr) console.warn('[voiceSession] link chat_conversation_id failed:', linkErr.message);
      });
  }

  return convo.id;
}

export async function updateVoiceSessionTurn({ supabase, sessionId, transcript }) {
  if (!sessionId) return;
  await supabase
    .from('voice_sessions')
    .update({
      turn_count: Math.ceil(transcript.length / 2),
      transcript,
    })
    .eq('id', sessionId);
}

export async function closeVoiceSession({ supabase, sessionId, transcript }) {
  if (!sessionId) return;
  await supabase
    .from('voice_sessions')
    .update({ ended_at: new Date().toISOString(), transcript })
    .eq('id', sessionId);
}
